// ─────────────────────────────────────────────────────────────────────────────
// colourSchemes.ts — Colour scheme definitions + CSS variable applicator
//
// Each scheme defines the dark-mode colour palette for the CV page.
// In light mode only the accent colours change; backgrounds stay light.
// CSS variables are injected onto :root at runtime so Tailwind classes that
// reference them (e.g. bg-[var(--color-hero)]) update instantly.
// ─────────────────────────────────────────────────────────────────────────────

export interface ColourScheme {
  id:           string;
  name:         string;
  /** Dark hero / page background */
  hero:         string;
  /** Primary accent colour */
  accent:       string;
  /** Lighter accent variant */
  accentLight:  string;
  /** Dark card/surface colour */
  surface:      string;
  /** Dark border colour */
  border:       string;
  /** Mid-dark colour used for secondary dark elements */
  navyMid:      string;
  /** If true, applies html[data-cyberpunk="true"] neon glow effects */
  cyberpunk?:   boolean;
}

export const COLOUR_SCHEMES: ColourScheme[] = [
  {
    id: 'navy-gold', name: 'Navy & Gold',
    hero: '#0D1B2A', accent: '#D4AF37', accentLight: '#F0D060',
    surface: '#1A2942', border: '#2D4A6B', navyMid: '#1E3A5F',
  },
  {
    id: 'midnight', name: 'Midnight',
    hero: '#12102b', accent: '#7C3AED', accentLight: '#A78BFA',
    surface: '#1e1b4b', border: '#3730a3', navyMid: '#2d2a5e',
  },
  {
    id: 'forest', name: 'Forest',
    hero: '#0a1a09', accent: '#22C55E', accentLight: '#4ADE80',
    surface: '#13261a', border: '#166534', navyMid: '#1a3a22',
  },
  {
    id: 'crimson', name: 'Crimson',
    hero: '#1a0707', accent: '#EF4444', accentLight: '#FCA5A5',
    surface: '#2a1010', border: '#7f1d1d', navyMid: '#3f1414',
  },
  {
    id: 'ocean', name: 'Ocean',
    hero: '#071929', accent: '#0EA5E9', accentLight: '#38BDF8',
    surface: '#0e2840', border: '#0e4a6e', navyMid: '#0f3a5a',
  },
  {
    id: 'slate', name: 'Slate',
    hero: '#0c1220', accent: '#38BDF8', accentLight: '#7DD3FC',
    surface: '#16203a', border: '#1e3a5c', navyMid: '#1a2e4a',
  },
  {
    id: 'charcoal', name: 'Charcoal',
    hero: '#0f0f0f', accent: '#F59E0B', accentLight: '#FCD34D',
    surface: '#1c1c1c', border: '#333333', navyMid: '#2a2a2a',
  },
  {
    id: 'rose', name: 'Rose',
    hero: '#1a0810', accent: '#F43F5E', accentLight: '#FB7185',
    surface: '#2a1020', border: '#881337', navyMid: '#3f1528',
  },
  {
    id: 'jungle', name: 'Jungle',
    hero: '#060f07', accent: '#84CC16', accentLight: '#BEF264',
    surface: '#0d1c0f', border: '#365314', navyMid: '#1a3019',
  },
  {
    id: 'royal', name: 'Royal',
    hero: '#160e28', accent: '#A855F7', accentLight: '#D8B4FE',
    surface: '#1e1535', border: '#581c87', navyMid: '#2e1a50',
  },
  {
    id: 'arctic', name: 'Arctic',
    hero: '#051220', accent: '#67E8F9', accentLight: '#A5F3FC',
    surface: '#0d1e2e', border: '#155e75', navyMid: '#0f3044',
  },
  {
    id: 'copper', name: 'Copper',
    hero: '#1a0f05', accent: '#F97316', accentLight: '#FDBA74',
    surface: '#2a1c0a', border: '#7c2d12', navyMid: '#401f0a',
  },

  // ── Scholarly — Collegiate gold (default for Scholarly layout) ───────────
  {
    id: 'collegiate-gold', name: 'Collegiate Gold',
    hero: '#001135', accent: '#C9A84C', accentLight: '#E8CA70',
    surface: '#001E55', border: '#003A9A', navyMid: '#002A7A',
  },

  // ── Warzone — Dark earth / rust (default for Warzone layout) ─────────────
  {
    id: 'warzone-rust', name: 'Warzone Rust',
    hero: '#0F0800', accent: '#A0785A', accentLight: '#C49A78',
    surface: '#1E1205', border: '#503820', navyMid: '#3A2810',
  },

  // ── Layout-default schemes ────────────────────────────────────────────────
  {
    id: 'superhero-cosmic', name: 'Superhero Cosmic',
    hero: '#0A0014', accent: '#FFD700', accentLight: '#FFF176',
    surface: '#14002A', border: '#4A0080', navyMid: '#220050',
  },
  {
    id: 'stark-scifi', name: 'Stark Sci-Fi',
    hero: '#020B14', accent: '#00DFFF', accentLight: '#80EFFF',
    surface: '#041A28', border: '#005A70', navyMid: '#063040',
  },
  {
    id: 'cosy-fantasy', name: 'Cosy Fantasy',
    hero: '#1B0F05', accent: '#D4A853', accentLight: '#E8C97A',
    surface: '#2E1E0A', border: '#7A4E1A', navyMid: '#4A2E0E',
  },
  {
    id: 'empire-imperial', name: 'Empire Imperial',
    hero: '#120005', accent: '#C9A84C', accentLight: '#E8CA70',
    surface: '#22000D', border: '#6B0020', navyMid: '#3A0015',
  },
  {
    id: 'cartoon-pop', name: 'Cartoon Pop',
    hero: '#1A0044', accent: '#FF6B35', accentLight: '#FF9D6F',
    surface: '#280060', border: '#7B00CC', navyMid: '#3D0088',
  },
  {
    id: 'pixel-retro', name: 'Pixel Retro',
    hero: '#0F0F23', accent: '#39FF14', accentLight: '#A0FF80',
    surface: '#1A1A38', border: '#3A3A7A', navyMid: '#252550',
  },

  // ── Cyberpunk Neon variants ───────────────────────────────────────────────
  {
    id: 'neon-aqua', name: 'Neon Aqua',
    hero: '#020C16', accent: '#00F5FF', accentLight: '#7FFBFF',
    surface: '#051825', border: '#004D55', navyMid: '#083040',
    cyberpunk: true,
  },
  {
    id: 'cyber-pulse', name: 'Cyber Pulse',
    hero: '#070010', accent: '#BF00FF', accentLight: '#E57FFF',
    surface: '#120020', border: '#5C0080', navyMid: '#250040',
    cyberpunk: true,
  },
  {
    id: 'matrix', name: 'Matrix',
    hero: '#000A00', accent: '#00FF41', accentLight: '#80FFB0',
    surface: '#001200', border: '#004D10', navyMid: '#002800',
    cyberpunk: true,
  },
  {
    id: 'hologram', name: 'Hologram',
    hero: '#00040F', accent: '#00DFFF', accentLight: '#80EFFF',
    surface: '#000D20', border: '#003D55', navyMid: '#001830',
    cyberpunk: true,
  },
  {
    id: 'electric', name: 'Electric',
    hero: '#050008', accent: '#FF00FF', accentLight: '#FF80FF',
    surface: '#0D0015', border: '#550055', navyMid: '#200028',
    cyberpunk: true,
  },
];

// ── CSS variable applicator ───────────────────────────────────────────────────

/** Nudge a hex colour by ±amount per channel (clamped 0–255) */
function adjustHex(hex: string, amount: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, v));
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const toHex = (n: number) => clamp(n).toString(16).padStart(2, '0');
  return `#${toHex(r + amount)}${toHex(g + amount)}${toHex(b + amount)}`;
}

/**
 * Apply a colour scheme to the document root CSS variables.
 * Called whenever the user selects a new scheme or the light/dark mode changes.
 */
export function applyColourScheme(schemeId: string, isDark: boolean): void {
  const scheme = COLOUR_SCHEMES.find((s) => s.id === schemeId);
  if (!scheme) return;

  const root = document.documentElement;

  // Toggle cyberpunk neon effects via data attribute
  if (scheme.cyberpunk) {
    root.setAttribute('data-cyberpunk', 'true');
  } else {
    root.removeAttribute('data-cyberpunk');
  }

  // Accent colours apply in both light and dark mode
  root.style.setProperty('--color-accent',       scheme.accent);
  root.style.setProperty('--color-accent-light',  scheme.accentLight);
  // Hero bg used by HeroSection via var(--color-hero)
  root.style.setProperty('--color-hero',          scheme.hero);

  if (isDark) {
    root.style.setProperty('--color-bg',          scheme.hero);
    root.style.setProperty('--color-bg-alt',       adjustHex(scheme.hero, -8));
    root.style.setProperty('--color-surface',      scheme.surface);
    root.style.setProperty('--color-surface-alt',  adjustHex(scheme.surface, 10));
    root.style.setProperty('--color-border',       scheme.border);
    root.style.setProperty('--color-navy',         scheme.hero);
    root.style.setProperty('--color-navy-mid',     scheme.navyMid);
  } else {
    // Light mode: restore standard light backgrounds, only accent changes
    root.style.setProperty('--color-bg',          '#F8FAFC');
    root.style.setProperty('--color-bg-alt',       '#F1F5F9');
    root.style.setProperty('--color-surface',      '#FFFFFF');
    root.style.setProperty('--color-surface-alt',  '#F8FAFC');
    root.style.setProperty('--color-border',       '#E2E8F0');
    root.style.setProperty('--color-navy',         '#0D1B2A');
    root.style.setProperty('--color-navy-mid',     '#1E3A5F');
  }
}
